
import React, { useState, useEffect, useMemo, useRef, FC, SVGProps } from 'react';
import ConfigurationPanel from './components/ConfigurationPanel';
import PreviewPanel from './components/PreviewPanel';
import LoginModal from './components/LoginModal';
import UserManagement from './components/UserManagement';
import StatsModal from './components/StatsModal';
import SetupWizardRd from './components/SetupWizardRd';
import SetupWizard, { WIZARD_SHAPES, type WizardShape, type WizardShapeDefault } from './components/SetupWizard';
import { ICONS as defaultIcons, FONTS, DEFAULT_APP_LOGO } from './constants';
import type { LabelConfig, LabelVariant, VariantSettings, IconType, User, IconCategory, CustomBackground, BackgroundCategory, CustomShape, CustomTemplate } from './types';
import DynamicSvgIcon from './components/DynamicSvgIcon';
import { authService } from './lib/auth';
import { settingsService, UserPermissions } from './lib/settings';
import { getMaxSafeBleed } from './lib/svgGenerator';
import { lockedTemplateHeight } from './lib/templateRatio';
import { deleteLegacyDraft } from './lib/draftStorage';

const DEF_WIDTH = 26;

const INITIAL_VARIANT_SETTINGS: Record<string, VariantSettings> = {
  'square-sm': { iconScale: 1.0, fontScale: 1.0, themeId: '', textAlign: 'center', isBold: true, enableShading: true, customWidth: DEF_WIDTH, customHeight: DEF_WIDTH, customCount: 14, visible: true, userToggleable: true, showSelectionToggle: true },
  'circle-sm': { shapeId: 'circle', iconScale: 0.9, fontScale: 0.9, themeId: '', textAlign: 'center', isBold: true, enableShading: true, customWidth: DEF_WIDTH, customHeight: DEF_WIDTH, customCount: 21, visible: true, userToggleable: true, showSelectionToggle: true },
  'rectangle-sm': { iconScale: 0.9, fontScale: 0.6, themeId: '', textAlign: 'center', textOffsetY: 2, isBold: true, enableShading: true, customWidth: DEF_WIDTH, customHeight: 12, customCount: 21, visible: true, userToggleable: true, showSelectionToggle: true },
  'rectangle-md': { iconScale: 1.1, fontScale: 0.6, themeId: '', textAlign: 'center', textOffsetY: 2, isBold: true, wrapText: true, enableShading: true, customWidth: DEF_WIDTH, customHeight: 16, customCount: 21, visible: true, userToggleable: true, showSelectionToggle: true },
  'rectangle-lg': { iconScale: 1.1, fontScale: 1.1, themeId: '', textAlign: 'center', textOffsetY: 3, iconOffsetY: -3, isBold: true, enableShading: true, customWidth: DEF_WIDTH, customHeight: 20, customCount: 14, visible: true, userToggleable: true, showSelectionToggle: true },
  'circle-lg': { shapeId: 'circle', iconScale: 1.0, fontScale: 1.0, themeId: '', textAlign: 'center', textOffsetY: 2, iconOffsetY: -2, isBold: true, wrapText: true, enableShading: true, customWidth: DEF_WIDTH, customHeight: DEF_WIDTH, customCount: 0, visible: true, userToggleable: true, showSelectionToggle: true },
};

const DEFAULT_CATEGORY_ID = 'general';

export const DEFAULT_CATEGORIES: IconCategory[] = [
  { id: 'general', name: 'Obecné' },
  { id: 'basic', name: 'Základní' },
  { id: 'animals', name: 'Zvířata' },
  { id: 'transport', name: 'Doprava' },
  { id: 'sport', name: 'Sport' },
  { id: 'other', name: 'Ostatní' }
];

const DEFAULT_BG_CATEGORIES: BackgroundCategory[] = [
    { id: 'general', name: 'Obecné' },
    { id: 'patterns', name: 'Vzory' },
    { id: 'textures', name: 'Textury' }
];

const DEFAULT_PERMISSIONS: UserPermissions = {
    allowAiGenerator: true,
    allowIconUpload: false,
    allowGlobalColors: true,
    allowFontSelection: true,
    allowStickerDimensions: true,
    allowStickerCount: true,
    allowTextStyling: true,
    allowFineTuning: true,
    allowFullImageUpload: true,
    allowSheetPositioning: false,
    allowStickerPositioning: false,
    allowNameField: true,
    allowClassNameField: true,
    allowSharpCorners: true,
    showGlobalNameField: true,
    showGlobalClassNameField: true,
    allowQuickSettings: true,
    showGlobalSettings: true,
    allowAddSticker: true
};

const createComponentFromSvgString = (svgString: string): FC<SVGProps<SVGSVGElement>> => {
  return (props) => <DynamicSvgIcon svgString={svgString} {...props} />;
};

const safeDefaultIcon = { ...defaultIcons[0], categoryId: defaultIcons[0].categoryId || 'basic' };

function App() {
  const [isMobile, setIsMobile] = useState(window.matchMedia('(max-width: 1023px)').matches);
  // Wide screens get the three-column editor (list of stickers | sheet | settings of the selected sticker).
  const [isWide, setIsWide] = useState(window.matchMedia('(min-width: 1280px)').matches);
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // The shop's settings could not be read (offline, blocked reCAPTCHA/App Check). The app then runs on built-in
  // defaults — including a return URL of a different product page — so ordering is switched off and the customer told.
  const [settingsLoadFailed, setSettingsLoadFailed] = useState(false);
  const [showWizard, setShowWizard] = useState(true);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false); 
  const [alertModal, setAlertModal] = useState<string | null>(null);

  const [categories, setCategories] = useState<IconCategory[]>(DEFAULT_CATEGORIES);
  const [bgCategories, setBgCategories] = useState<BackgroundCategory[]>(DEFAULT_BG_CATEGORIES);
  const [customBackgrounds, setCustomBackgrounds] = useState<CustomBackground[]>([]);
  // Backgrounds a regular user uploaded this session — never persisted to Firestore,
  // so they stay invisible to every other user/session. Merged with the shared
  // (admin-uploaded) list below purely for local rendering/export.
  const [localCustomBackgrounds, setLocalCustomBackgrounds] = useState<CustomBackground[]>([]);
  const [customTemplates, setCustomTemplates] = useState<CustomTemplate[]>([]);
  const [customShapes, setCustomShapes] = useState<CustomShape[]>([]);
  const [allIcons, setAllIcons] = useState<IconType[]>(() => defaultIcons.map(i => ({ ...i, categoryId: i.categoryId || DEFAULT_CATEGORY_ID })));
  // Icons the admin put into the default sticker settings live only there, not in the icon library — they are
  // just as much "the shop's own" icons, so the statistics must count them under their own id too.
  const [adminDefaultIconIds, setAdminDefaultIconIds] = useState<string[]>([]);
  // Sticker types the admin hid in the default settings — the setup wizard must not switch these back on.
  const [adminHiddenVariantIds, setAdminHiddenVariantIds] = useState<string[]>([]);
  // What the admin saved as the default for each sticker slot (not the built-ins they never touched). The
  // setup wizard starts each shape from this: a slot the admin turned into a template stays that template,
  // and keeps the admin's icon and sticker count.
  const [adminSavedVariantSettings, setAdminSavedVariantSettings] = useState<Record<string, VariantSettings>>({});
  const wizardAllowedShapes = useMemo(() => {
    const allowed = WIZARD_SHAPES.filter(s => !adminHiddenVariantIds.includes(s.variantId)).map(s => s.id);
    return allowed.length > 0 ? allowed : WIZARD_SHAPES.map(s => s.id);
  }, [adminHiddenVariantIds]);
  const knownIconIds = useMemo(() => new Set([...allIcons.map(i => i.id), ...adminDefaultIconIds]), [allIcons, adminDefaultIconIds]);
  const [deletedIconIds, setDeletedIconIds] = useState<string[]>([]);

  const [appLogo, setAppLogo] = useState<string>(DEFAULT_APP_LOGO);
  const [faviconUrl, setFaviconUrl] = useState<string>('https://www.aaapapir.cz/img/favicon.ico');
  const [appTitle, setAppTitle] = useState<string>('Samolepící štítky a jmenovky');
  const [appSubtitle, setAppSubtitle] = useState<string>('Konfigurátor jmenovek');
  const [nameLabel, setNameLabel] = useState<string>('Jméno a příjmení');
  const [classNameLabel, setClassNameLabel] = useState<string>('Třída / Poznámka');
  const [iconLabel, setIconLabel] = useState<string>('Ikona');
  const [addStickerButtonLabel, setAddStickerButtonLabel] = useState<string>('Přidat další samolepku');
  const [sharedDesignLabel, setSharedDesignLabel] = useState<string>('Společný design archu');
  const [selectIconButtonLabel, setSelectIconButtonLabel] = useState<string>('Vybrat ikonu');
  const [defaultNameValue, setDefaultNameValue] = useState<string>('Petr Novák');
  const [defaultClassNameValue, setDefaultClassNameValue] = useState<string>('1.A');
  const [uploadButtonLabel, setUploadButtonLabel] = useState<string>('Nahrát');
  const [wizardKicker, setWizardKicker] = useState<string>('');
  const [wizardTitle, setWizardTitle] = useState<string>('');
  const [wizardIntro, setWizardIntro] = useState<string>('');
  const [showBackgroundsToPublic, setShowBackgroundsToPublic] = useState<boolean>(true);
  const [allowUserUploads, setAllowUserUploads] = useState<boolean>(true);
  const [adminReturnUrl, setAdminReturnUrl] = useState<string | null>('https://www.aaapapir.cz/konfigurator-sady-samolepek-do-skoly-a-skolky-1_d112792.html');
  const [driveFolderId, setDriveFolderId] = useState<string>('0AGtH0ioYz2hdUk9PVA');
  const [userPermissions, setUserPermissions] = useState<UserPermissions>(DEFAULT_PERMISSIONS);
  const [sheetWidth, setSheetWidth] = useState<number>(210);
  const [sheetHeight, setSheetHeight] = useState<number>(297);
  const [bleedEnabled, setBleedEnabled] = useState<boolean>(true);
  const [bleedMm, setBleedMm] = useState<number>(1);
  const [cutLinesEnabled, setCutLinesEnabled] = useState<boolean>(true);
  const [aiGeneratorDailyLimit, setAiGeneratorDailyLimit] = useState<number>(10);
  const [aiGeneratorGlobalDailyLimit, setAiGeneratorGlobalDailyLimit] = useState<number>(2000);

  const [labelConfig, setLabelConfig] = useState<LabelConfig>(() => ({
    name: 'Petr Novák',
    className: '',
    icon: safeDefaultIcon,
    fontFamily: 'Arial',
    themeId: 'blue',
    variantSettings: INITIAL_VARIANT_SETTINGS,
  }));

  // What a brand-new visitor would see (built-ins merged with admin-configured
  // defaults, once those load) — "Začít znovu" resets back to exactly this.
  const defaultLabelConfigRef = useRef<LabelConfig>(labelConfig);

  // An SVG template keeps the artwork's proportions (see lib/templateRatio.ts). Whichever way a sticker got its
  // width and height — the setup wizard, the shape tabs, the width field, the admin defaults,
  // templates that only finished loading afterwards — the height is brought back in line with the width here.
  useEffect(() => {
    setLabelConfig(prev => {
      let next = prev.variantSettings;
      for (const [id, s] of Object.entries(prev.variantSettings) as [string, VariantSettings][]) {
        if (!s.templateId) continue;
        const height = lockedTemplateHeight(customTemplates.find(t => t.id === s.templateId), s.customWidth || DEF_WIDTH);
        if (height !== null && Math.abs(height - (s.customHeight ?? 0)) > 0.001) next = { ...next, [id]: { ...s, customHeight: height } };
      }
      return next === prev.variantSettings ? prev : { ...prev, variantSettings: next };
    });
  }, [labelConfig.variantSettings, customTemplates]);

  useEffect(() => {
    const fontsToLoad = new Set<string>();
    if (labelConfig.fontFamily && labelConfig.fontFamily !== 'Arial') fontsToLoad.add(labelConfig.fontFamily);
    (Object.values(labelConfig.variantSettings) as VariantSettings[]).forEach(s => {
        if (s.fontFamily && s.fontFamily !== 'Arial') fontsToLoad.add(s.fontFamily);
    });
    if (fontsToLoad.size > 0) {
      const fontId = 'dynamic-fonts-link';
      let link = document.getElementById(fontId) as HTMLLinkElement;
      if (!link) {
        link = document.createElement('link');
        link.id = fontId;
        link.rel = 'stylesheet';
        document.head.appendChild(link);
      }
      const familiesParam = Array.from(fontsToLoad).map(f => `family=${f.replace(/ /g, '+')}:wght@400;700`).join('&');
      link.href = `https://fonts.googleapis.com/css2?${familiesParam}&display=swap`;
    }
  }, [labelConfig]);

  useEffect(() => {
      const unsubscribe = authService.onAuthStateChange((user) => setCurrentUser(user));
      return () => unsubscribe();
  }, []);

  useEffect(() => {
    const handleResize = () => { setIsMobile(window.matchMedia('(max-width: 1023px)').matches); setIsWide(window.matchMedia('(min-width: 1280px)').matches); };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
      const loadData = async () => {
          setIsLoading(true);
          try {
              const { general, assets, failed } = await settingsService.loadGlobalData();
              if (failed) {
                  setSettingsLoadFailed(true);
                  setAdminReturnUrl(null);
              }
              if (general.appTitle) setAppTitle(general.appTitle);
              if (general.appSubtitle) setAppSubtitle(general.appSubtitle);
              if (general.appLogo) setAppLogo(general.appLogo);
              if (general.faviconUrl) setFaviconUrl(general.faviconUrl);
              if (general.nameLabel) setNameLabel(general.nameLabel);
              if (general.classNameLabel) setClassNameLabel(general.classNameLabel);
              if (general.iconLabel) setIconLabel(general.iconLabel);
              if (general.addStickerButtonLabel) setAddStickerButtonLabel(general.addStickerButtonLabel);
              if (general.sharedDesignLabel) setSharedDesignLabel(general.sharedDesignLabel);
              if (general.selectIconButtonLabel) setSelectIconButtonLabel(general.selectIconButtonLabel);
              if (general.defaultNameValue) setDefaultNameValue(general.defaultNameValue);
              if (general.defaultClassNameValue) setDefaultClassNameValue(general.defaultClassNameValue);
              if (general.uploadButtonLabel) setUploadButtonLabel(general.uploadButtonLabel);
              if (general.wizardKicker) setWizardKicker(general.wizardKicker);
              if (general.wizardTitle) setWizardTitle(general.wizardTitle);
              if (general.wizardIntro) setWizardIntro(general.wizardIntro);
              if (general.adminReturnUrl) setAdminReturnUrl(general.adminReturnUrl);
              if (general.driveFolderId) setDriveFolderId(general.driveFolderId);
              if (general.showBackgroundsToPublic !== undefined) setShowBackgroundsToPublic(general.showBackgroundsToPublic);
              if (general.allowUserUploads !== undefined) setAllowUserUploads(general.allowUserUploads);
              if (general.iconCategories) setCategories(general.iconCategories);
              if (general.bgCategories) setBgCategories(general.bgCategories);
              if (general.permissions) setUserPermissions({ ...DEFAULT_PERMISSIONS, ...general.permissions });
              if (general.sheetWidth) setSheetWidth(general.sheetWidth);
              if (general.sheetHeight) setSheetHeight(general.sheetHeight);
              if (general.bleedEnabled !== undefined) setBleedEnabled(general.bleedEnabled);
              if (general.bleedMm !== undefined) setBleedMm(general.bleedMm);
              if (general.cutLinesEnabled !== undefined) setCutLinesEnabled(general.cutLinesEnabled);
              if (general.aiGeneratorDailyLimit !== undefined) setAiGeneratorDailyLimit(general.aiGeneratorDailyLimit);
              if (general.aiGeneratorGlobalDailyLimit !== undefined) setAiGeneratorGlobalDailyLimit(general.aiGeneratorGlobalDailyLimit);

              const deletedIds = (general as any).deletedIconIds || [];
              setDeletedIconIds(deletedIds);

              if (assets.customBackgrounds) setCustomBackgrounds(assets.customBackgrounds);
              if (assets.customTemplates) setCustomTemplates(assets.customTemplates);
              if (assets.customShapes) setCustomShapes(assets.customShapes);
              
              const storedIcons = assets.customIcons || [];
              const defaultIds = new Set(defaultIcons.map(i => i.id));
              
              const trulyCustom = storedIcons.filter(s => !defaultIds.has(s.id)).map(icon => ({
                  ...icon,
                  isCustom: true,
                  component: (icon.dataType === 'svg' && typeof icon.data === 'string' && !icon.component) ? createComponentFromSvgString(icon.data) : undefined
                }));

              const mergedIcons = defaultIcons
                  .filter(d => !deletedIds.includes(d.id))
                  .map(d => {
                      const stored = storedIcons.find(s => s.id === d.id);
                      return { ...d, categoryId: stored?.categoryId || d.categoryId || DEFAULT_CATEGORY_ID };
                  });
              
              setAllIcons([...mergedIcons, ...trulyCustom]);

              const resolvedVariantSettings = { ...INITIAL_VARIANT_SETTINGS, ...general.defaultVariantSettings };
              // One-time backfill: circle presets used to be identified by their id
              // (e.g. "circle-sm") rather than shapeId. Admin defaults saved to Firestore
              // before this change may still lack shapeId, which would now render as a
              // rectangle since shape is no longer inferred from the id.
              Object.keys(resolvedVariantSettings).forEach(id => {
                  const s = resolvedVariantSettings[id];
                  if (id.includes('circle') && !s.shapeId && !s.isFullImage && !s.templateId) {
                      resolvedVariantSettings[id] = { ...s, shapeId: 'circle' };
                  }
              });
              defaultLabelConfigRef.current = { ...defaultLabelConfigRef.current, variantSettings: resolvedVariantSettings };
              setAdminSavedVariantSettings(general.defaultVariantSettings || {});
              setAdminHiddenVariantIds(Object.entries(resolvedVariantSettings).filter(([, v]) => (v as VariantSettings).visible === false).map(([id]) => id));
              setAdminDefaultIconIds(Object.values(resolvedVariantSettings).map(v => v.customIcon?.id).filter((id): id is string => !!id));
              if (general.defaultVariantSettings) {
                  setLabelConfig(prev => ({ ...prev, variantSettings: resolvedVariantSettings }));
              }
              if (general.defaultNameValue) {
                  defaultLabelConfigRef.current = { ...defaultLabelConfigRef.current, name: general.defaultNameValue };
                  setLabelConfig(prev => ({ ...prev, name: general.defaultNameValue! }));
              }
              if (general.defaultClassNameValue) {
                  defaultLabelConfigRef.current = { ...defaultLabelConfigRef.current, className: general.defaultClassNameValue };
                  setLabelConfig(prev => ({ ...prev, className: general.defaultClassNameValue! }));
              }
          } catch (e) { console.error("[App] Load error:", e); }
          finally { setIsLoading(false); }
      };
      loadData();
  }, [currentUser]);

  const [orderToken] = useState<string>(() => {
      const urlParams = new URLSearchParams(window.location.search);
      const raw = urlParams.get('token');
      // Only trust externally-supplied tokens matching our own generation format
      // (gen-<timestamp>) — this is what gets echoed back to the eshop as
      // order_token, so keeping it to a predictable shape lets the eshop safely
      // validate it too. Anything else (garbage, bot noise) falls back to a
      // freshly generated token instead of being forwarded as-is.
      return (raw && /^gen-\d{1,20}$/.test(raw)) ? raw : `gen-${Date.now()}`;
  });
  const [quantity, setQuantity] = useState(1);

  useEffect(() => { deleteLegacyDraft(); }, []);

  useEffect(() => {
    let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = faviconUrl;
  }, [faviconUrl]);

  const handleIconsChange = async (updatedIcons: IconType[]) => {
    const updatedIds = new Set(updatedIcons.map(i => i.id));
    const newlyDeleted = defaultIcons
        .map(i => i.id)
        .filter(id => !updatedIds.has(id));

    setAllIcons([...updatedIcons]);
    setDeletedIconIds(newlyDeleted);

    // Only admins persist changes to the shared icon catalog — a regular
    // customer's AI-generated/uploaded icon stays local to their own order
    // (already applied via onSettingChange) and Firestore rules reject the
    // write anyway, since saveCustomIcons can also delete catalog entries.
    const isAdminOrSuper = !!currentUser && (currentUser.role === 'administrator' || currentUser.role === 'superadministrator');
    if (!isAdminOrSuper) return;

    try {
        await settingsService.saveCustomIcons(updatedIcons);
        await settingsService.saveGeneralSettings({ deletedIconIds: newlyDeleted } as any);
    }
    catch (e: any) {
        console.error("Save icons error:", e);
        setAlertModal(`Chyba ukládání ikon: ${e.message}`);
    }
  };

  const handleCategoriesChange = async (updatedCats: IconCategory[]) => {
      setCategories(updatedCats);
      try { await settingsService.saveGeneralSettings({ iconCategories: updatedCats }); }
      catch (e: any) { setAlertModal(`Chyba ukládání složek: ${e.message}`); }
  };

  const handleBgCategoriesChange = async (updatedCats: BackgroundCategory[]) => {
      setBgCategories(updatedCats);
      try { await settingsService.saveGeneralSettings({ bgCategories: updatedCats }); }
      catch (e: any) { setAlertModal(`Chyba ukládání složek pozadí: ${e.message}`); }
  };

  const handleCustomBackgroundsChange = async (updatedBgs: CustomBackground[]) => {
      setCustomBackgrounds(updatedBgs);
      try { await settingsService.saveCustomBackgrounds(updatedBgs); }
      catch (e: any) { setAlertModal(`Chyba ukládání pozadí: ${e.message}`); }
  };

  const handleAddLocalBackgrounds = (newBgs: CustomBackground[]) => {
      setLocalCustomBackgrounds(prev => [...prev, ...newBgs.map(bg => ({ ...bg, isLocal: true }))]);
  };

  const effectiveCustomBackgrounds = useMemo(
      () => [...customBackgrounds, ...localCustomBackgrounds],
      [customBackgrounds, localCustomBackgrounds]
  );

  const handleCustomTemplatesChange = async (updatedTemplates: CustomTemplate[]) => {
      setCustomTemplates(updatedTemplates);
      try { await settingsService.saveCustomTemplates(updatedTemplates); }
      catch (e: any) { setAlertModal(`Chyba ukládání šablon: ${e.message}`); }
  };

  const handleCustomShapesChange = async (updatedShapes: CustomShape[]) => {
      setCustomShapes(updatedShapes);
      try { await settingsService.saveCustomShapes(updatedShapes); }
      catch (e: any) { setAlertModal(`Chyba ukládání tvarů: ${e.message}`); }
  };

  const handleVariantSettingChange = (variantId: string, keyOrChanges: keyof VariantSettings | Partial<VariantSettings>, value?: any) => {
    let updates: Partial<VariantSettings> = typeof keyOrChanges === 'string' ? { [keyOrChanges]: value } : keyOrChanges;
    setLabelConfig(prev => {
      const newVariantSettings = { ...prev.variantSettings, [variantId]: { ...prev.variantSettings[variantId], ...updates } };
      if ('visible' in updates) {
        settingsService.loadGlobalData().then(({ general }) => {
          const { customName, customClassName, ...cleanSettings } = newVariantSettings[variantId];
          settingsService.saveGeneralSettings({
            defaultVariantSettings: { ...general.defaultVariantSettings || {}, [variantId]: cleanSettings }
          }).catch(e => console.error('Chyba ukládání visible:', e));
        });
      }
      return { ...prev, variantSettings: newVariantSettings };
    });
  };

  const handleResetVariant = (variantId: string) => {
    if (!window.confirm("Opravdu chcete tuto samolepku resetovat?")) return;
    
    let baseSettings: VariantSettings | undefined;
    if (variantId in INITIAL_VARIANT_SETTINGS) {
        baseSettings = INITIAL_VARIANT_SETTINGS[variantId];
    } else if (variantId.includes('square')) {
        baseSettings = INITIAL_VARIANT_SETTINGS['square-sm'];
    } else if (variantId.includes('circle')) {
        baseSettings = INITIAL_VARIANT_SETTINGS['circle-sm'];
    } else {
        baseSettings = INITIAL_VARIANT_SETTINGS['rectangle-md'];
    }

    if (baseSettings) {
        setLabelConfig(prev => ({
            ...prev,
            variantSettings: {
                ...prev.variantSettings,
                [variantId]: { ...baseSettings! }
            }
        }));
    }
  };

  const handleResetSheet = () => {
    if (!window.confirm("Opravdu chcete zahodit všechny úpravy a začít od výchozího stavu?")) return;
    setLabelConfig(defaultLabelConfigRef.current);
    setQuantity(1);
    setLocalCustomBackgrounds([]);
  };

  // Completing the wizard fully replaces variantSettings, so — just like
  // "Začít znovu" — reopening it after the sheet is already configured needs
  // a confirmation; unlike the very first automatic prompt, this one is a
  // deliberate mid-session click that would otherwise silently discard work.
  const handleOpenWizard = () => {
    if (!window.confirm("Průvodce přepíše aktuální nastavení archu (barvy, počty kusů, vzory pozadí...). Opravdu chcete pokračovat?")) return;
    setShowWizard(true);
  };

  const handleAddSticker = () => {
      const newId = `custom-${Date.now()}`;
      setLabelConfig(prev => ({
          ...prev,
          variantSettings: {
              ...prev.variantSettings,
              [newId]: { ...INITIAL_VARIANT_SETTINGS['rectangle-md'], visible: true, customCount: 1 }
          }
      }));
  };

  const handleRemoveSticker = (id: string) => {
      setLabelConfig(prev => {
          const updated = { ...prev.variantSettings };
          delete updated[id];
          return { ...prev, variantSettings: updated };
      });
      // The variant may have been persisted as a system default (via the visibility
      // toggle or "Nastavit jako výchozí"), so it must be purged there too — otherwise
      // it reappears on next load when defaultVariantSettings is merged back in.
      settingsService.removeVariantDefault(id).catch(e => console.error('Chyba mazání výchozí samolepky:', e));
  };

  const handleSaveGeneralSetting = async (key: string, value: any) => {
      try {
          await settingsService.saveGeneralSettings({ [key]: value });
      } catch (e: any) {
          setAlertModal(`Chyba ukládání nastavení: ${e.message}`);
      }
  };

  const handleSheetSizeChange = async (width: number, height: number) => {
      setSheetWidth(width);
      setSheetHeight(height);
      try {
          await settingsService.saveGeneralSettings({ sheetWidth: width, sheetHeight: height });
      } catch (e: any) {
          setAlertModal(`Chyba ukládání velikosti archu: ${e.message}`);
      }
  };

  const handleBleedEnabledChange = async (enabled: boolean) => {
      setBleedEnabled(enabled);
      try {
          await settingsService.saveGeneralSettings({ bleedEnabled: enabled });
      } catch (e: any) {
          setAlertModal(`Chyba ukládání nastavení spadávky: ${e.message}`);
      }
  };

  const handleBleedMmChange = async (mm: number) => {
      setBleedMm(mm);
      try {
          await settingsService.saveGeneralSettings({ bleedMm: mm });
      } catch (e: any) {
          setAlertModal(`Chyba ukládání nastavení spadávky: ${e.message}`);
      }
  };

  const handleCutLinesEnabledChange = async (enabled: boolean) => {
      setCutLinesEnabled(enabled);
      try {
          await settingsService.saveGeneralSettings({ cutLinesEnabled: enabled });
      } catch (e: any) {
          setAlertModal(`Chyba ukládání nastavení ořezových cest: ${e.message}`);
      }
  };

  const handleAiGeneratorDailyLimitChange = async (limit: number) => {
      setAiGeneratorDailyLimit(limit);
      try {
          await settingsService.saveGeneralSettings({ aiGeneratorDailyLimit: limit });
      } catch (e: any) {
          setAlertModal(`Chyba ukládání limitu AI generování: ${e.message}`);
      }
  };

  const handleAiGeneratorGlobalDailyLimitChange = async (limit: number) => {
      setAiGeneratorGlobalDailyLimit(limit);
      try {
          await settingsService.saveGeneralSettings({ aiGeneratorGlobalDailyLimit: limit });
      } catch (e: any) {
          setAlertModal(`Chyba ukládání celkového denního limitu AI generování: ${e.message}`);
      }
  };

  const handlePermissionChange = async (key: keyof UserPermissions, value: boolean) => {
      const newPermissions = { ...userPermissions, [key]: value };
      setUserPermissions(newPermissions);
      try {
          await settingsService.saveGeneralSettings({ permissions: newPermissions });
      } catch (e: any) {
          setAlertModal(`Chyba ukládání oprávnění: ${e.message}`);
      }
  };

  const wizardShapeDefaults = useMemo(() => {
    const result: Partial<Record<WizardShape, WizardShapeDefault>> = {};
    for (const shape of WIZARD_SHAPES) {
      const saved = adminSavedVariantSettings[shape.variantId];
      if (!saved) continue;
      const template = saved.templateId ? customTemplates.find(t => t.id === saved.templateId) : undefined;
      const w = saved.customWidth || DEF_WIDTH;
      result[shape.id] = {
        icon: saved.customIcon,
        count: saved.customCount,
        template: template ? { id: template.id, w, h: lockedTemplateHeight(template, w) ?? saved.customHeight ?? w } : undefined,
      };
    }
    return result;
  }, [adminSavedVariantSettings, customTemplates]);

  const handleWizardComplete = (wizardName: string, wizardClassName: string, wizardThemeId: string, wizardShapes: WizardShape[], wizardShapeIcons: Partial<Record<WizardShape, IconType>>, wizardShapeThemes: Partial<Record<WizardShape, string>>, wizardShapeWrapModes: Partial<Record<WizardShape, '1'|'2'|'3'>>, selectedTemplate?: { id: string; width: number; height: number }, slotTemplates?: Partial<Record<WizardShape, { id: string; w: number; h: number }>>) => {
    const CONTENT_HEIGHT = sheetHeight - 14; // sheet size minus margins
    const CONTENT_WIDTH = sheetWidth - 14;
    const GAP = 2;
    const heightPerShape = CONTENT_HEIGHT / wizardShapes.length;

    setLabelConfig(prev => {
      const newSettings: Record<string, VariantSettings> = Object.fromEntries(
        (Object.entries(prev.variantSettings) as [string, VariantSettings][]).map(([id, s]) => [id, { ...s, visible: false, themeId: '' }])
      );
      for (const shape of wizardShapes) {
        // "template" has no fixed WIZARD_SHAPES entry (its size comes from whichever
        // template image was picked, not a constant) — handled separately below.
        if (shape === 'template') continue;
        const cfg = WIZARD_SHAPES.find(s => s.id === shape)!;
        const shapeDefault = wizardShapeDefaults[shape];
        const template = slotTemplates?.[shape] ?? shapeDefault?.template;
        const w = template ? template.w : cfg.w;
        const h = template ? template.h : cfg.h;
        const cols = Math.floor(CONTENT_WIDTH / (w + GAP));
        const rows = Math.max(1, Math.floor(heightPerShape / (h + GAP)));
        const wrapMode = wizardShapeWrapModes[shape] ?? '1';
        newSettings[cfg.variantId] = {
          ...newSettings[cfg.variantId],
          visible: true,
          // The wizard creates exactly what the admin set up for this slot — reset any shapeId/template
          // state the slot may have picked up from manual editing since, so a leftover
          // "square-sm switched to Kruh" doesn't quietly survive as a "Čtverec".
          shapeId: template ? undefined : cfg.shapeId,
          isFullImage: !!template,
          templateId: template?.id,
          fullImageData: undefined,
          ...(template ? { customWidth: w, customHeight: h } : {}),
          // The admin's count when they saved one for this slot, otherwise as many as fill the sheet.
          customCount: shapeDefault?.count || cols * rows,
          // Empty string (not wizardThemeId) when the user didn't pick a shape-specific
          // color, so the sticker keeps inheriting the shared sheet color afterwards
          // instead of getting silently pinned to it.
          themeId: wizardShapeThemes[shape] ?? '',
          ...(wizardShapeIcons[shape] ? { customIcon: wizardShapeIcons[shape] } : {}),
          wrapText: wrapMode !== '1',
          forceTextSplit: wrapMode === '3',
          ...(wrapMode === '2' ? { fontScale: 0.7 } : wrapMode === '3' ? { fontScale: 0.6 } : {}),
        };
      }
      if (selectedTemplate) {
        const cols = Math.floor(CONTENT_WIDTH / (selectedTemplate.width + GAP));
        const rows = Math.max(1, Math.floor(heightPerShape / (selectedTemplate.height + GAP)));
        const wrapMode = wizardShapeWrapModes['template'] ?? '1';
        newSettings[`template-${Date.now()}`] = {
          iconScale: 1.0,
          fontScale: 1.0,
          visible: true,
          customCount: cols * rows,
          themeId: wizardShapeThemes['template'] ?? '',
          isFullImage: true,
          templateId: selectedTemplate.id,
          customWidth: selectedTemplate.width,
          customHeight: selectedTemplate.height,
          ...(wizardShapeIcons['template'] ? { customIcon: wizardShapeIcons['template'] } : {}),
          wrapText: wrapMode !== '1',
          forceTextSplit: wrapMode === '3',
          ...(wrapMode === '2' ? { fontScale: 0.7 } : wrapMode === '3' ? { fontScale: 0.6 } : {}),
        };
      }
      return { ...prev, name: wizardName, className: wizardClassName, themeId: wizardThemeId, customColor: undefined, variantSettings: newSettings };
    });
    setShowWizard(false);
  };

  // Redesigned wizard (návrh 02): it hands over the finished stickers; everything else on the sheet gets hidden.
  const handleWizardRdComplete = (wizardName: string, wizardClassName: string, stickers: Record<string, VariantSettings>, firstKey: string | null) => {
    setLabelConfig(prev => {
      const newSettings: Record<string, VariantSettings> = Object.fromEntries(
        (Object.entries(prev.variantSettings) as [string, VariantSettings][]).map(([id, s]) => [id, { ...s, visible: false, themeId: '' }])
      );
      for (const [key, settings] of Object.entries(stickers)) newSettings[key] = settings;
      return { ...prev, name: wizardName, className: wizardClassName, themeId: 'blue', customColor: undefined, variantSettings: newSettings };
    });
    setShowWizard(false);
    if (firstKey) setSelectedVariantId(firstKey);
  };

  const isAdminOrSuper = !!currentUser && (currentUser.role === 'administrator' || currentUser.role === 'superadministrator');
  const effectiveBleedMm = bleedEnabled ? Math.min(bleedMm, getMaxSafeBleed(labelConfig)) : 0;

  return (
    <div className="rd flex flex-col min-h-screen font-sans overflow-x-clip">
      <header className="rd-header shrink-0">
        <div className="rd-header-inner">
          <div className="flex items-center gap-4 min-w-0">
            <img src={appLogo} alt="Logo" className="rd-logo" />
            <div className="min-w-0">
               <h1 className="rd-title">{appTitle}</h1>
               <p className="rd-subtitle">{appSubtitle}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button type="button" onClick={handleResetSheet} className="rd-btn-ghost" title="Zahodit všechny úpravy a začít od výchozího stavu">
              <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
              <span className="hidden sm:inline">Začít znovu</span>
            </button>
            {(isAdminOrSuper || userPermissions.allowQuickSettings) && (
            <button onClick={handleOpenWizard} className="rd-btn-secondary">
              <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
              Rychlé nastavení
            </button>
            )}
          </div>
        </div>
      </header>
      {settingsLoadFailed && (
        <div role="alert" className="w-full bg-amber-100 border-b-2 border-amber-400 text-amber-950 px-4 py-3 text-xs sm:text-sm font-bold flex flex-wrap items-center justify-center gap-3 text-center">
          <span>Nepodařilo se načíst nastavení obchodu, proto teď nelze odeslat objednávku. Zkuste stránku obnovit. Pokud potíže trvají, zkontrolujte, zda vám nebrání blokátor reklam nebo soukromí (Google reCAPTCHA).</span>
          <button type="button" onClick={() => window.location.reload()} className="px-3 py-1.5 bg-amber-900 text-white rounded-md uppercase tracking-widest text-[10px] font-black">Obnovit stránku</button>
        </div>
      )}
      <main className="rd-main flex-grow w-full">
        {isWide ? (
        <div className="rd-grid3">
          <aside className="rd-col-left">
            <ConfigurationPanel section="left" selectedVariantId={selectedVariantId} onSelectVariant={setSelectedVariantId} 
              config={labelConfig} onConfigChange={setLabelConfig}
              icons={allIcons} onIconsChange={handleIconsChange}
              categories={categories} onCategoriesChange={handleCategoriesChange}
              bgCategories={bgCategories} onBgCategoriesChange={handleBgCategoriesChange}
              customBackgrounds={effectiveCustomBackgrounds} onCustomBackgroundsChange={handleCustomBackgroundsChange} onAddLocalBackgrounds={handleAddLocalBackgrounds}
              customTemplates={customTemplates} onCustomTemplatesChange={handleCustomTemplatesChange}
              customShapes={customShapes} onCustomShapesChange={handleCustomShapesChange}
              currentUser={currentUser} orderToken={orderToken} quantity={quantity} onQuantityChange={setQuantity}
              returnUrl={adminReturnUrl} onFinalUrlGenerated={() => {}}
              adminReturnUrl={adminReturnUrl} onAdminReturnUrlChange={(val) => { setAdminReturnUrl(val); handleSaveGeneralSetting('adminReturnUrl', val); }}
              driveFolderId={driveFolderId} onDriveFolderIdChange={(val) => { setDriveFolderId(val); handleSaveGeneralSetting('driveFolderId', val); }}
              setAlertModal={setAlertModal} isMobile={isMobile}
              onAppLogoChange={(val) => { setAppLogo(val); handleSaveGeneralSetting('appLogo', val); }}
              faviconUrl={faviconUrl} onFaviconUrlChange={(val) => { setFaviconUrl(val); handleSaveGeneralSetting('faviconUrl', val); }} 
              appTitle={appTitle} onAppTitleChange={(val) => { setAppTitle(val); handleSaveGeneralSetting('appTitle', val); }}
              appSubtitle={appSubtitle} onAppSubtitleChange={(val) => { setAppSubtitle(val); handleSaveGeneralSetting('appSubtitle', val); }}
              nameLabel={nameLabel} onNameLabelChange={(val) => { setNameLabel(val); handleSaveGeneralSetting('nameLabel', val); }}
              classNameLabel={classNameLabel} onClassNameLabelChange={(val) => { setClassNameLabel(val); handleSaveGeneralSetting('classNameLabel', val); }}
              iconLabel={iconLabel} onIconLabelChange={(val) => { setIconLabel(val); handleSaveGeneralSetting('iconLabel', val); }}
              addStickerButtonLabel={addStickerButtonLabel} onAddStickerButtonLabelChange={(val) => { setAddStickerButtonLabel(val); handleSaveGeneralSetting('addStickerButtonLabel', val); }}
              sharedDesignLabel={sharedDesignLabel} onSharedDesignLabelChange={(val) => { setSharedDesignLabel(val); handleSaveGeneralSetting('sharedDesignLabel', val); }}
              selectIconButtonLabel={selectIconButtonLabel} onSelectIconButtonLabelChange={(val) => { setSelectIconButtonLabel(val); handleSaveGeneralSetting('selectIconButtonLabel', val); }}
              defaultNameValue={defaultNameValue} onDefaultNameValueChange={(val) => { setDefaultNameValue(val); handleSaveGeneralSetting('defaultNameValue', val); }}
              defaultClassNameValue={defaultClassNameValue} onDefaultClassNameValueChange={(val) => { setDefaultClassNameValue(val); handleSaveGeneralSetting('defaultClassNameValue', val); }}
              uploadButtonLabel={uploadButtonLabel} onUploadButtonLabelChange={(val) => { setUploadButtonLabel(val); handleSaveGeneralSetting('uploadButtonLabel', val); }}
              wizardKicker={wizardKicker} onWizardKickerChange={(val) => { setWizardKicker(val); handleSaveGeneralSetting('wizardKicker', val); }}
              wizardTitle={wizardTitle} onWizardTitleChange={(val) => { setWizardTitle(val); handleSaveGeneralSetting('wizardTitle', val); }}
              wizardIntro={wizardIntro} onWizardIntroChange={(val) => { setWizardIntro(val); handleSaveGeneralSetting('wizardIntro', val); }}
              showBackgroundsToPublic={showBackgroundsToPublic} onShowBackgroundsToPublicChange={(val) => { setShowBackgroundsToPublic(val); handleSaveGeneralSetting('showBackgroundsToPublic', val); }}
              allowUserUploads={allowUserUploads} onAllowUserUploadsChange={(val) => { setAllowUserUploads(val); handleSaveGeneralSetting('allowUserUploads', val); }}
              onVariantSettingChange={handleVariantSettingChange}
              onResetVariant={handleResetVariant}
              onAddSticker={handleAddSticker}
              onRemoveSticker={handleRemoveSticker}
              permissions={userPermissions}
              onPermissionChange={handlePermissionChange}
              sheetWidth={sheetWidth} sheetHeight={sheetHeight} onSheetSizeChange={handleSheetSizeChange}
              bleedEnabled={bleedEnabled} bleedMm={bleedMm} onBleedEnabledChange={handleBleedEnabledChange} onBleedMmChange={handleBleedMmChange}
              effectiveBleedMm={effectiveBleedMm}
              cutLinesEnabled={cutLinesEnabled} onCutLinesEnabledChange={handleCutLinesEnabledChange}
              aiGeneratorDailyLimit={aiGeneratorDailyLimit} onAiGeneratorDailyLimitChange={handleAiGeneratorDailyLimitChange}
              aiGeneratorGlobalDailyLimit={aiGeneratorGlobalDailyLimit} onAiGeneratorGlobalDailyLimitChange={handleAiGeneratorGlobalDailyLimitChange}
            />
            <div className="rd-sheet-slot">
                <PreviewPanel
                  config={labelConfig} quantity={quantity} isMobile={isMobile}
                  returnUrl={adminReturnUrl} orderToken={orderToken}
                  onFinalUrlGenerated={() => {}}
                  onQuantityChange={setQuantity} setAlertModal={setAlertModal}
                  isAdminMode={isAdminOrSuper}
                  driveFolderId={driveFolderId}
                  customBackgrounds={effectiveCustomBackgrounds}
                  customTemplates={customTemplates}
                  customShapes={customShapes}
                  appLogo={appLogo}
                  sheetWidth={sheetWidth} sheetHeight={sheetHeight}
                  bleedMm={effectiveBleedMm}
                  cutLinesEnabled={cutLinesEnabled}
                  knownIconIds={knownIconIds}
                  compact
                />
            </div>
          </aside>
          <section className="rd-col-center">
            <div className="rd-sticky">
                <ConfigurationPanel section="stage" selectedVariantId={selectedVariantId} onSelectVariant={setSelectedVariantId} 
              config={labelConfig} onConfigChange={setLabelConfig}
              icons={allIcons} onIconsChange={handleIconsChange}
              categories={categories} onCategoriesChange={handleCategoriesChange}
              bgCategories={bgCategories} onBgCategoriesChange={handleBgCategoriesChange}
              customBackgrounds={effectiveCustomBackgrounds} onCustomBackgroundsChange={handleCustomBackgroundsChange} onAddLocalBackgrounds={handleAddLocalBackgrounds}
              customTemplates={customTemplates} onCustomTemplatesChange={handleCustomTemplatesChange}
              customShapes={customShapes} onCustomShapesChange={handleCustomShapesChange}
              currentUser={currentUser} orderToken={orderToken} quantity={quantity} onQuantityChange={setQuantity}
              returnUrl={adminReturnUrl} onFinalUrlGenerated={() => {}}
              adminReturnUrl={adminReturnUrl} onAdminReturnUrlChange={(val) => { setAdminReturnUrl(val); handleSaveGeneralSetting('adminReturnUrl', val); }}
              driveFolderId={driveFolderId} onDriveFolderIdChange={(val) => { setDriveFolderId(val); handleSaveGeneralSetting('driveFolderId', val); }}
              setAlertModal={setAlertModal} isMobile={isMobile}
              onAppLogoChange={(val) => { setAppLogo(val); handleSaveGeneralSetting('appLogo', val); }}
              faviconUrl={faviconUrl} onFaviconUrlChange={(val) => { setFaviconUrl(val); handleSaveGeneralSetting('faviconUrl', val); }} 
              appTitle={appTitle} onAppTitleChange={(val) => { setAppTitle(val); handleSaveGeneralSetting('appTitle', val); }}
              appSubtitle={appSubtitle} onAppSubtitleChange={(val) => { setAppSubtitle(val); handleSaveGeneralSetting('appSubtitle', val); }}
              nameLabel={nameLabel} onNameLabelChange={(val) => { setNameLabel(val); handleSaveGeneralSetting('nameLabel', val); }}
              classNameLabel={classNameLabel} onClassNameLabelChange={(val) => { setClassNameLabel(val); handleSaveGeneralSetting('classNameLabel', val); }}
              iconLabel={iconLabel} onIconLabelChange={(val) => { setIconLabel(val); handleSaveGeneralSetting('iconLabel', val); }}
              addStickerButtonLabel={addStickerButtonLabel} onAddStickerButtonLabelChange={(val) => { setAddStickerButtonLabel(val); handleSaveGeneralSetting('addStickerButtonLabel', val); }}
              sharedDesignLabel={sharedDesignLabel} onSharedDesignLabelChange={(val) => { setSharedDesignLabel(val); handleSaveGeneralSetting('sharedDesignLabel', val); }}
              selectIconButtonLabel={selectIconButtonLabel} onSelectIconButtonLabelChange={(val) => { setSelectIconButtonLabel(val); handleSaveGeneralSetting('selectIconButtonLabel', val); }}
              defaultNameValue={defaultNameValue} onDefaultNameValueChange={(val) => { setDefaultNameValue(val); handleSaveGeneralSetting('defaultNameValue', val); }}
              defaultClassNameValue={defaultClassNameValue} onDefaultClassNameValueChange={(val) => { setDefaultClassNameValue(val); handleSaveGeneralSetting('defaultClassNameValue', val); }}
              uploadButtonLabel={uploadButtonLabel} onUploadButtonLabelChange={(val) => { setUploadButtonLabel(val); handleSaveGeneralSetting('uploadButtonLabel', val); }}
              wizardKicker={wizardKicker} onWizardKickerChange={(val) => { setWizardKicker(val); handleSaveGeneralSetting('wizardKicker', val); }}
              wizardTitle={wizardTitle} onWizardTitleChange={(val) => { setWizardTitle(val); handleSaveGeneralSetting('wizardTitle', val); }}
              wizardIntro={wizardIntro} onWizardIntroChange={(val) => { setWizardIntro(val); handleSaveGeneralSetting('wizardIntro', val); }}
              showBackgroundsToPublic={showBackgroundsToPublic} onShowBackgroundsToPublicChange={(val) => { setShowBackgroundsToPublic(val); handleSaveGeneralSetting('showBackgroundsToPublic', val); }}
              allowUserUploads={allowUserUploads} onAllowUserUploadsChange={(val) => { setAllowUserUploads(val); handleSaveGeneralSetting('allowUserUploads', val); }}
              onVariantSettingChange={handleVariantSettingChange}
              onResetVariant={handleResetVariant}
              onAddSticker={handleAddSticker}
              onRemoveSticker={handleRemoveSticker}
              permissions={userPermissions}
              onPermissionChange={handlePermissionChange}
              sheetWidth={sheetWidth} sheetHeight={sheetHeight} onSheetSizeChange={handleSheetSizeChange}
              bleedEnabled={bleedEnabled} bleedMm={bleedMm} onBleedEnabledChange={handleBleedEnabledChange} onBleedMmChange={handleBleedMmChange}
              effectiveBleedMm={effectiveBleedMm}
              cutLinesEnabled={cutLinesEnabled} onCutLinesEnabledChange={handleCutLinesEnabledChange}
              aiGeneratorDailyLimit={aiGeneratorDailyLimit} onAiGeneratorDailyLimitChange={handleAiGeneratorDailyLimitChange}
              aiGeneratorGlobalDailyLimit={aiGeneratorGlobalDailyLimit} onAiGeneratorGlobalDailyLimitChange={handleAiGeneratorGlobalDailyLimitChange}
            />
            </div>
          </section>
          <aside className="rd-col-right">
            <ConfigurationPanel section="right" selectedVariantId={selectedVariantId} onSelectVariant={setSelectedVariantId} 
              config={labelConfig} onConfigChange={setLabelConfig}
              icons={allIcons} onIconsChange={handleIconsChange}
              categories={categories} onCategoriesChange={handleCategoriesChange}
              bgCategories={bgCategories} onBgCategoriesChange={handleBgCategoriesChange}
              customBackgrounds={effectiveCustomBackgrounds} onCustomBackgroundsChange={handleCustomBackgroundsChange} onAddLocalBackgrounds={handleAddLocalBackgrounds}
              customTemplates={customTemplates} onCustomTemplatesChange={handleCustomTemplatesChange}
              customShapes={customShapes} onCustomShapesChange={handleCustomShapesChange}
              currentUser={currentUser} orderToken={orderToken} quantity={quantity} onQuantityChange={setQuantity}
              returnUrl={adminReturnUrl} onFinalUrlGenerated={() => {}}
              adminReturnUrl={adminReturnUrl} onAdminReturnUrlChange={(val) => { setAdminReturnUrl(val); handleSaveGeneralSetting('adminReturnUrl', val); }}
              driveFolderId={driveFolderId} onDriveFolderIdChange={(val) => { setDriveFolderId(val); handleSaveGeneralSetting('driveFolderId', val); }}
              setAlertModal={setAlertModal} isMobile={isMobile}
              onAppLogoChange={(val) => { setAppLogo(val); handleSaveGeneralSetting('appLogo', val); }}
              faviconUrl={faviconUrl} onFaviconUrlChange={(val) => { setFaviconUrl(val); handleSaveGeneralSetting('faviconUrl', val); }} 
              appTitle={appTitle} onAppTitleChange={(val) => { setAppTitle(val); handleSaveGeneralSetting('appTitle', val); }}
              appSubtitle={appSubtitle} onAppSubtitleChange={(val) => { setAppSubtitle(val); handleSaveGeneralSetting('appSubtitle', val); }}
              nameLabel={nameLabel} onNameLabelChange={(val) => { setNameLabel(val); handleSaveGeneralSetting('nameLabel', val); }}
              classNameLabel={classNameLabel} onClassNameLabelChange={(val) => { setClassNameLabel(val); handleSaveGeneralSetting('classNameLabel', val); }}
              iconLabel={iconLabel} onIconLabelChange={(val) => { setIconLabel(val); handleSaveGeneralSetting('iconLabel', val); }}
              addStickerButtonLabel={addStickerButtonLabel} onAddStickerButtonLabelChange={(val) => { setAddStickerButtonLabel(val); handleSaveGeneralSetting('addStickerButtonLabel', val); }}
              sharedDesignLabel={sharedDesignLabel} onSharedDesignLabelChange={(val) => { setSharedDesignLabel(val); handleSaveGeneralSetting('sharedDesignLabel', val); }}
              selectIconButtonLabel={selectIconButtonLabel} onSelectIconButtonLabelChange={(val) => { setSelectIconButtonLabel(val); handleSaveGeneralSetting('selectIconButtonLabel', val); }}
              defaultNameValue={defaultNameValue} onDefaultNameValueChange={(val) => { setDefaultNameValue(val); handleSaveGeneralSetting('defaultNameValue', val); }}
              defaultClassNameValue={defaultClassNameValue} onDefaultClassNameValueChange={(val) => { setDefaultClassNameValue(val); handleSaveGeneralSetting('defaultClassNameValue', val); }}
              uploadButtonLabel={uploadButtonLabel} onUploadButtonLabelChange={(val) => { setUploadButtonLabel(val); handleSaveGeneralSetting('uploadButtonLabel', val); }}
              wizardKicker={wizardKicker} onWizardKickerChange={(val) => { setWizardKicker(val); handleSaveGeneralSetting('wizardKicker', val); }}
              wizardTitle={wizardTitle} onWizardTitleChange={(val) => { setWizardTitle(val); handleSaveGeneralSetting('wizardTitle', val); }}
              wizardIntro={wizardIntro} onWizardIntroChange={(val) => { setWizardIntro(val); handleSaveGeneralSetting('wizardIntro', val); }}
              showBackgroundsToPublic={showBackgroundsToPublic} onShowBackgroundsToPublicChange={(val) => { setShowBackgroundsToPublic(val); handleSaveGeneralSetting('showBackgroundsToPublic', val); }}
              allowUserUploads={allowUserUploads} onAllowUserUploadsChange={(val) => { setAllowUserUploads(val); handleSaveGeneralSetting('allowUserUploads', val); }}
              onVariantSettingChange={handleVariantSettingChange}
              onResetVariant={handleResetVariant}
              onAddSticker={handleAddSticker}
              onRemoveSticker={handleRemoveSticker}
              permissions={userPermissions}
              onPermissionChange={handlePermissionChange}
              sheetWidth={sheetWidth} sheetHeight={sheetHeight} onSheetSizeChange={handleSheetSizeChange}
              bleedEnabled={bleedEnabled} bleedMm={bleedMm} onBleedEnabledChange={handleBleedEnabledChange} onBleedMmChange={handleBleedMmChange}
              effectiveBleedMm={effectiveBleedMm}
              cutLinesEnabled={cutLinesEnabled} onCutLinesEnabledChange={handleCutLinesEnabledChange}
              aiGeneratorDailyLimit={aiGeneratorDailyLimit} onAiGeneratorDailyLimitChange={handleAiGeneratorDailyLimitChange}
              aiGeneratorGlobalDailyLimit={aiGeneratorGlobalDailyLimit} onAiGeneratorGlobalDailyLimitChange={handleAiGeneratorGlobalDailyLimitChange}
            />
          </aside>
        </div>
        ) : (
        <div className="lg:grid lg:grid-cols-12 lg:gap-6 flex flex-col gap-4">
          <div className="lg:col-span-7 w-full order-first lg:order-last">
            <div className="lg:sticky lg:top-[84px] h-fit flex flex-col gap-4">
                <ConfigurationPanel section="stage" selectedVariantId={selectedVariantId} onSelectVariant={setSelectedVariantId} 
              config={labelConfig} onConfigChange={setLabelConfig}
              icons={allIcons} onIconsChange={handleIconsChange}
              categories={categories} onCategoriesChange={handleCategoriesChange}
              bgCategories={bgCategories} onBgCategoriesChange={handleBgCategoriesChange}
              customBackgrounds={effectiveCustomBackgrounds} onCustomBackgroundsChange={handleCustomBackgroundsChange} onAddLocalBackgrounds={handleAddLocalBackgrounds}
              customTemplates={customTemplates} onCustomTemplatesChange={handleCustomTemplatesChange}
              customShapes={customShapes} onCustomShapesChange={handleCustomShapesChange}
              currentUser={currentUser} orderToken={orderToken} quantity={quantity} onQuantityChange={setQuantity}
              returnUrl={adminReturnUrl} onFinalUrlGenerated={() => {}}
              adminReturnUrl={adminReturnUrl} onAdminReturnUrlChange={(val) => { setAdminReturnUrl(val); handleSaveGeneralSetting('adminReturnUrl', val); }}
              driveFolderId={driveFolderId} onDriveFolderIdChange={(val) => { setDriveFolderId(val); handleSaveGeneralSetting('driveFolderId', val); }}
              setAlertModal={setAlertModal} isMobile={isMobile}
              onAppLogoChange={(val) => { setAppLogo(val); handleSaveGeneralSetting('appLogo', val); }}
              faviconUrl={faviconUrl} onFaviconUrlChange={(val) => { setFaviconUrl(val); handleSaveGeneralSetting('faviconUrl', val); }} 
              appTitle={appTitle} onAppTitleChange={(val) => { setAppTitle(val); handleSaveGeneralSetting('appTitle', val); }}
              appSubtitle={appSubtitle} onAppSubtitleChange={(val) => { setAppSubtitle(val); handleSaveGeneralSetting('appSubtitle', val); }}
              nameLabel={nameLabel} onNameLabelChange={(val) => { setNameLabel(val); handleSaveGeneralSetting('nameLabel', val); }}
              classNameLabel={classNameLabel} onClassNameLabelChange={(val) => { setClassNameLabel(val); handleSaveGeneralSetting('classNameLabel', val); }}
              iconLabel={iconLabel} onIconLabelChange={(val) => { setIconLabel(val); handleSaveGeneralSetting('iconLabel', val); }}
              addStickerButtonLabel={addStickerButtonLabel} onAddStickerButtonLabelChange={(val) => { setAddStickerButtonLabel(val); handleSaveGeneralSetting('addStickerButtonLabel', val); }}
              sharedDesignLabel={sharedDesignLabel} onSharedDesignLabelChange={(val) => { setSharedDesignLabel(val); handleSaveGeneralSetting('sharedDesignLabel', val); }}
              selectIconButtonLabel={selectIconButtonLabel} onSelectIconButtonLabelChange={(val) => { setSelectIconButtonLabel(val); handleSaveGeneralSetting('selectIconButtonLabel', val); }}
              defaultNameValue={defaultNameValue} onDefaultNameValueChange={(val) => { setDefaultNameValue(val); handleSaveGeneralSetting('defaultNameValue', val); }}
              defaultClassNameValue={defaultClassNameValue} onDefaultClassNameValueChange={(val) => { setDefaultClassNameValue(val); handleSaveGeneralSetting('defaultClassNameValue', val); }}
              uploadButtonLabel={uploadButtonLabel} onUploadButtonLabelChange={(val) => { setUploadButtonLabel(val); handleSaveGeneralSetting('uploadButtonLabel', val); }}
              wizardKicker={wizardKicker} onWizardKickerChange={(val) => { setWizardKicker(val); handleSaveGeneralSetting('wizardKicker', val); }}
              wizardTitle={wizardTitle} onWizardTitleChange={(val) => { setWizardTitle(val); handleSaveGeneralSetting('wizardTitle', val); }}
              wizardIntro={wizardIntro} onWizardIntroChange={(val) => { setWizardIntro(val); handleSaveGeneralSetting('wizardIntro', val); }}
              showBackgroundsToPublic={showBackgroundsToPublic} onShowBackgroundsToPublicChange={(val) => { setShowBackgroundsToPublic(val); handleSaveGeneralSetting('showBackgroundsToPublic', val); }}
              allowUserUploads={allowUserUploads} onAllowUserUploadsChange={(val) => { setAllowUserUploads(val); handleSaveGeneralSetting('allowUserUploads', val); }}
              onVariantSettingChange={handleVariantSettingChange}
              onResetVariant={handleResetVariant}
              onAddSticker={handleAddSticker}
              onRemoveSticker={handleRemoveSticker}
              permissions={userPermissions}
              onPermissionChange={handlePermissionChange}
              sheetWidth={sheetWidth} sheetHeight={sheetHeight} onSheetSizeChange={handleSheetSizeChange}
              bleedEnabled={bleedEnabled} bleedMm={bleedMm} onBleedEnabledChange={handleBleedEnabledChange} onBleedMmChange={handleBleedMmChange}
              effectiveBleedMm={effectiveBleedMm}
              cutLinesEnabled={cutLinesEnabled} onCutLinesEnabledChange={handleCutLinesEnabledChange}
              aiGeneratorDailyLimit={aiGeneratorDailyLimit} onAiGeneratorDailyLimitChange={handleAiGeneratorDailyLimitChange}
              aiGeneratorGlobalDailyLimit={aiGeneratorGlobalDailyLimit} onAiGeneratorGlobalDailyLimitChange={handleAiGeneratorGlobalDailyLimitChange}
            />
                <PreviewPanel
                  config={labelConfig} quantity={quantity} isMobile={isMobile}
                  returnUrl={adminReturnUrl} orderToken={orderToken}
                  onFinalUrlGenerated={() => {}}
                  onQuantityChange={setQuantity} setAlertModal={setAlertModal}
                  isAdminMode={isAdminOrSuper}
                  driveFolderId={driveFolderId}
                  customBackgrounds={effectiveCustomBackgrounds}
                  customTemplates={customTemplates}
                  customShapes={customShapes}
                  appLogo={appLogo}
                  sheetWidth={sheetWidth} sheetHeight={sheetHeight}
                  bleedMm={effectiveBleedMm}
                  cutLinesEnabled={cutLinesEnabled}
                  knownIconIds={knownIconIds}
                  compact
                />
            </div>
          </div>
          <div className="lg:col-span-5 w-full lg:order-first">
            <ConfigurationPanel section="all" selectedVariantId={selectedVariantId} onSelectVariant={setSelectedVariantId} 
              config={labelConfig} onConfigChange={setLabelConfig}
              icons={allIcons} onIconsChange={handleIconsChange}
              categories={categories} onCategoriesChange={handleCategoriesChange}
              bgCategories={bgCategories} onBgCategoriesChange={handleBgCategoriesChange}
              customBackgrounds={effectiveCustomBackgrounds} onCustomBackgroundsChange={handleCustomBackgroundsChange} onAddLocalBackgrounds={handleAddLocalBackgrounds}
              customTemplates={customTemplates} onCustomTemplatesChange={handleCustomTemplatesChange}
              customShapes={customShapes} onCustomShapesChange={handleCustomShapesChange}
              currentUser={currentUser} orderToken={orderToken} quantity={quantity} onQuantityChange={setQuantity}
              returnUrl={adminReturnUrl} onFinalUrlGenerated={() => {}}
              adminReturnUrl={adminReturnUrl} onAdminReturnUrlChange={(val) => { setAdminReturnUrl(val); handleSaveGeneralSetting('adminReturnUrl', val); }}
              driveFolderId={driveFolderId} onDriveFolderIdChange={(val) => { setDriveFolderId(val); handleSaveGeneralSetting('driveFolderId', val); }}
              setAlertModal={setAlertModal} isMobile={isMobile}
              onAppLogoChange={(val) => { setAppLogo(val); handleSaveGeneralSetting('appLogo', val); }}
              faviconUrl={faviconUrl} onFaviconUrlChange={(val) => { setFaviconUrl(val); handleSaveGeneralSetting('faviconUrl', val); }} 
              appTitle={appTitle} onAppTitleChange={(val) => { setAppTitle(val); handleSaveGeneralSetting('appTitle', val); }}
              appSubtitle={appSubtitle} onAppSubtitleChange={(val) => { setAppSubtitle(val); handleSaveGeneralSetting('appSubtitle', val); }}
              nameLabel={nameLabel} onNameLabelChange={(val) => { setNameLabel(val); handleSaveGeneralSetting('nameLabel', val); }}
              classNameLabel={classNameLabel} onClassNameLabelChange={(val) => { setClassNameLabel(val); handleSaveGeneralSetting('classNameLabel', val); }}
              iconLabel={iconLabel} onIconLabelChange={(val) => { setIconLabel(val); handleSaveGeneralSetting('iconLabel', val); }}
              addStickerButtonLabel={addStickerButtonLabel} onAddStickerButtonLabelChange={(val) => { setAddStickerButtonLabel(val); handleSaveGeneralSetting('addStickerButtonLabel', val); }}
              sharedDesignLabel={sharedDesignLabel} onSharedDesignLabelChange={(val) => { setSharedDesignLabel(val); handleSaveGeneralSetting('sharedDesignLabel', val); }}
              selectIconButtonLabel={selectIconButtonLabel} onSelectIconButtonLabelChange={(val) => { setSelectIconButtonLabel(val); handleSaveGeneralSetting('selectIconButtonLabel', val); }}
              defaultNameValue={defaultNameValue} onDefaultNameValueChange={(val) => { setDefaultNameValue(val); handleSaveGeneralSetting('defaultNameValue', val); }}
              defaultClassNameValue={defaultClassNameValue} onDefaultClassNameValueChange={(val) => { setDefaultClassNameValue(val); handleSaveGeneralSetting('defaultClassNameValue', val); }}
              uploadButtonLabel={uploadButtonLabel} onUploadButtonLabelChange={(val) => { setUploadButtonLabel(val); handleSaveGeneralSetting('uploadButtonLabel', val); }}
              wizardKicker={wizardKicker} onWizardKickerChange={(val) => { setWizardKicker(val); handleSaveGeneralSetting('wizardKicker', val); }}
              wizardTitle={wizardTitle} onWizardTitleChange={(val) => { setWizardTitle(val); handleSaveGeneralSetting('wizardTitle', val); }}
              wizardIntro={wizardIntro} onWizardIntroChange={(val) => { setWizardIntro(val); handleSaveGeneralSetting('wizardIntro', val); }}
              showBackgroundsToPublic={showBackgroundsToPublic} onShowBackgroundsToPublicChange={(val) => { setShowBackgroundsToPublic(val); handleSaveGeneralSetting('showBackgroundsToPublic', val); }}
              allowUserUploads={allowUserUploads} onAllowUserUploadsChange={(val) => { setAllowUserUploads(val); handleSaveGeneralSetting('allowUserUploads', val); }}
              onVariantSettingChange={handleVariantSettingChange}
              onResetVariant={handleResetVariant}
              onAddSticker={handleAddSticker}
              onRemoveSticker={handleRemoveSticker}
              permissions={userPermissions}
              onPermissionChange={handlePermissionChange}
              sheetWidth={sheetWidth} sheetHeight={sheetHeight} onSheetSizeChange={handleSheetSizeChange}
              bleedEnabled={bleedEnabled} bleedMm={bleedMm} onBleedEnabledChange={handleBleedEnabledChange} onBleedMmChange={handleBleedMmChange}
              effectiveBleedMm={effectiveBleedMm}
              cutLinesEnabled={cutLinesEnabled} onCutLinesEnabledChange={handleCutLinesEnabledChange}
              aiGeneratorDailyLimit={aiGeneratorDailyLimit} onAiGeneratorDailyLimitChange={handleAiGeneratorDailyLimitChange}
              aiGeneratorGlobalDailyLimit={aiGeneratorGlobalDailyLimit} onAiGeneratorGlobalDailyLimitChange={handleAiGeneratorGlobalDailyLimitChange}
            />
          </div>
        </div>
        )}
      </main>
      <footer className="rd-footer shrink-0">
          <div className="container mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
              <p className="order-2 md:order-1">© {new Date().getFullYear()} Tvořič školních samolepek</p>
              <div className="flex flex-wrap justify-center gap-6 order-1 md:order-2">
                {isAdminOrSuper && (
                    <>
                        <button onClick={() => setShowStatsModal(true)} className="text-blue-600 hover:underline">Statistiky</button>
                        <button onClick={() => setShowUserManagement(true)} className="text-blue-600 hover:underline">Uživatelé</button>
                    </>
                )}
                {currentUser ? (
                  <button onClick={() => authService.logout()} className="text-red-600 hover:underline">Odhlásit ({currentUser.email})</button>
                ) : (
                  <button onClick={() => setShowLoginModal(true)} className="text-gray-400 hover:text-black">Administrace</button>
                )}
              </div>
          </div>
      </footer>
      {showLoginModal && <LoginModal onLoginSuccess={() => setShowLoginModal(false)} onClose={() => setShowLoginModal(false)} />}
      {showUserManagement && currentUser && <UserManagement currentUser={currentUser} onClose={() => setShowUserManagement(false)} />}
      {showStatsModal && <StatsModal onClose={() => setShowStatsModal(false)} allIcons={allIcons} />}
      {alertModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[100] p-4">
            <div className="bg-white rounded-md border-4 border-black p-8 max-w-sm w-full shadow-2xl animate-fade-in">
                <p className="text-black font-black text-center text-lg mb-8 uppercase tracking-tighter">{alertModal}</p>
                <button onClick={() => setAlertModal(null)} className="w-full py-4 bg-black text-white font-black uppercase tracking-widest rounded-md hover:bg-red-600 transition-colors">Zavřít</button>
            </div>
        </div>
      )}
      {!isLoading && !settingsLoadFailed && showWizard && (isAdminOrSuper || userPermissions.allowQuickSettings) && (
        <SetupWizardRd
          nameLabel={nameLabel}
          classNameLabel={classNameLabel}
          defaultNameValue={defaultNameValue}
          defaultClassNameValue={defaultClassNameValue}
          title={wizardTitle}
          intro={wizardIntro}
          appLogo={appLogo}
          icons={allIcons}
          categories={categories}
          allowAi={userPermissions.allowAiGenerator}
          customTemplates={customTemplates}
          allowedShapes={wizardAllowedShapes}
          shapeDefaults={wizardShapeDefaults}
          baseVariantSettings={labelConfig.variantSettings}
          baseIcon={labelConfig.icon}
          sheetWidth={sheetWidth}
          sheetHeight={sheetHeight}
          onIconsChange={handleIconsChange}
          onComplete={handleWizardRdComplete}
          onSkip={() => setShowWizard(false)}
        />
      )}
      {isLoading && (
        <div className="fixed inset-0 bg-white z-[200] flex items-center justify-center">
             <div className="text-center">
                 <div className="w-12 h-12 border-4 border-black border-t-red-600 animate-spin mx-auto mb-4 rounded-full"></div>
                 <p className="text-black font-black uppercase tracking-widest text-xs">Načítání</p>
             </div>
        </div>
      )}
    </div>
  );
}
export default App;
